import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  DialogActions,
  DialogContent,
  FormControl,
  FormControlLabel,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography
} from '@mui/material';
import {
  Edit as EditIcon,
  Save as SaveIcon
} from '@mui/icons-material';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFnsV3';
import { ptBR } from 'date-fns/locale';
import PlatformDialog from '../../../molecules/PlatformDialog/PlatformDialog';
import PlatformDialogTitle from '../../../molecules/PlatformDialog/PlatformDialogTitle';
import DatePickerCalendarHeader from '../../../molecules/DatePickerCalendarHeader/DatePickerCalendarHeader';

const MEDIA_TYPE_OPTIONS = [
  { value: 'Online', label: 'Online (Site)' },
  { value: 'YouTube', label: 'YouTube' },
  { value: 'Twitter', label: 'Twitter/X' },
  { value: 'Instagram', label: 'Instagram' },
  { value: 'Threads', label: 'Threads' },
  { value: 'TikTok', label: 'TikTok' },
  { value: 'Facebook', label: 'Facebook' },
  { value: 'LinkedIn', label: 'LinkedIn' },
  { value: 'Pinterest', label: 'Pinterest' },
  { value: 'Jornal', label: 'Jornal' },
  { value: 'Revista', label: 'Revista' },
  { value: 'TV', label: 'TV' },
  { value: 'Radio', label: 'Radio' },
  { value: 'Podcast', label: 'Podcast' }
];

const CONTENT_TYPE_OPTIONS = {
  YouTube: [
    { value: 'video', label: 'Video' },
    { value: 'short', label: 'Short' }
  ],
  Twitter: [
    { value: 'tweet', label: 'Tweet' },
    { value: 'video', label: 'Video' }
  ],
  Instagram: [
    { value: 'post', label: 'Post' },
    { value: 'reel', label: 'Reel' },
    { value: 'story', label: 'Stories' },
    { value: 'video', label: 'Video' }
  ],
  TikTok: [
    { value: 'video', label: 'Video' }
  ],
  Facebook: [
    { value: 'post', label: 'Post' },
    { value: 'reel', label: 'Reel' },
    { value: 'photo', label: 'Foto' },
    { value: 'story', label: 'Stories' },
    { value: 'video', label: 'Video' }
  ],
  LinkedIn: [
    { value: 'post', label: 'Post' },
    { value: 'video', label: 'Video' },
    { value: 'article', label: 'Artigo' }
  ],
  Threads: [
    { value: 'post', label: 'Post' },
    { value: 'video', label: 'Video' }
  ],
  Pinterest: [
    { value: 'pin', label: 'Pin' },
    { value: 'video', label: 'Video' }
  ],
  Online: [
    { value: 'article', label: 'Artigo' },
    { value: 'video', label: 'Video' }
  ],
  Jornal: [
    { value: 'article', label: 'Materia' }
  ],
  Revista: [
    { value: 'article', label: 'Materia' }
  ],
  TV: [
    { value: 'video', label: 'Materia/Reportagem' }
  ],
  Radio: [
    { value: 'article', label: 'Mencao/Insercao' }
  ],
  Podcast: [
    { value: 'article', label: 'Episodio' }
  ]
};

const EXPOSURE_OPTIONS = [
  { value: 'dedicated', label: 'Dedicada' },
  { value: 'shared', label: 'Compartilhada' },
  { value: 'mention', label: 'Citacao' },
  { value: 'none', label: 'Sem exposicao' }
];

const INITIAL_ENABLED = {
  title: false,
  content: false,
  description: false,
  siteName: false,
  author: false,
  date: false,
  platform: false,
  exposure: false,
  section: false,
  image: false
};

const INITIAL_VALUES = {
  title: '',
  content: '',
  description: '',
  siteName: '',
  author: '',
  date: '',
  mediaType: 'Online',
  contentType: 'article',
  exposure: 'dedicated',
  section: '',
  image: ''
};

const toNoonUtcDate = (dateValue) => {
  if (!dateValue) return '';
  return `${dateValue}T12:00:00.000Z`;
};

const toLocalDate = (dateValue) => {
  if (!dateValue) return null;
  const [year, month, day] = dateValue.split('-').map(Number);
  return new Date(year, month - 1, day);
};

const toDateValue = (date) => {
  if (!date || Number.isNaN(date.getTime())) return '';
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const BulkEditArticlesDialog = ({
  open,
  articleIds = [],
  loading = false,
  onClose,
  onApply
}) => {
  const [enabledFields, setEnabledFields] = useState(INITIAL_ENABLED);
  const [values, setValues] = useState(INITIAL_VALUES);
  const [error, setError] = useState('');

  const contentTypeOptions = useMemo(
    () => CONTENT_TYPE_OPTIONS[values.mediaType] || CONTENT_TYPE_OPTIONS.Online,
    [values.mediaType]
  );

  useEffect(() => {
    if (!open) return;
    setEnabledFields(INITIAL_ENABLED);
    setValues(INITIAL_VALUES);
    setError('');
  }, [open]);

  const selectedFieldsCount = Object.values(enabledFields).filter(Boolean).length;

  const handleToggleField = (field) => {
    setEnabledFields(prev => ({
      ...prev,
      [field]: !prev[field]
    }));
    setError('');
  };

  const handleValueChange = (field) => (event) => {
    const nextValue = event.target.value;

    if (field === 'mediaType') {
      const nextContentTypeOptions = CONTENT_TYPE_OPTIONS[nextValue] || CONTENT_TYPE_OPTIONS.Online;
      setValues(prev => ({
        ...prev,
        mediaType: nextValue,
        contentType: nextContentTypeOptions[0]?.value || 'article'
      }));
      return;
    }

    setValues(prev => ({
      ...prev,
      [field]: nextValue
    }));
  };

  const buildPatch = () => {
    const patch = {};
    if (enabledFields.title) patch.title = values.title.trim();
    if (enabledFields.content) patch.content = values.content.trim();
    if (enabledFields.description) patch.description = values.description.trim();

    if (enabledFields.siteName) patch.siteName = values.siteName.trim();
    if (enabledFields.author) patch.author = values.author.trim();
    if (enabledFields.date) patch.date = toNoonUtcDate(values.date);
    if (enabledFields.platform) {
      patch.mediaType = values.mediaType;
      patch.contentType = values.contentType;
    }
    if (enabledFields.exposure) patch.exposure = values.exposure;
    if (enabledFields.section) patch.section = values.section.trim();
    if (enabledFields.image) patch.image = values.image.trim();

    return patch;
  };

  const handleApply = async () => {
    if (selectedFieldsCount === 0) {
      setError('Selecione pelo menos um campo para editar.');
      return;
    }

    if (enabledFields.date && !values.date) {
      setError('Informe uma data para aplicar em lote.');
      return;
    }

    const patch = buildPatch();
    await onApply?.(patch);
  };

  const renderTextField = (field, label, props = {}) => (
    <Box>
      <FormControlLabel
        control={
          <Checkbox
            checked={enabledFields[field]}
            onChange={() => handleToggleField(field)}
            disabled={loading}
          />
        }
        label={label}
      />
      <TextField
        fullWidth
        size="small"
        value={values[field]}
        onChange={handleValueChange(field)}
        disabled={!enabledFields[field] || loading}
        {...props}
      />
    </Box>
  );

  return (
    <PlatformDialog
      open={open}
      onClose={loading ? undefined : onClose}
      maxWidth="sm"
      fullWidth
    >
      <PlatformDialogTitle
        title="Editar campos em lote"
        subtitle={`${articleIds.length} artigo(s) selecionado(s)`}
        icon={<EditIcon color="primary" />}
        onClose={loading ? undefined : onClose}
      />

      <DialogContent sx={{ pt: 3.5 }}>
        <Alert severity="info" sx={{ mb: 2 }}>
          Apenas os campos marcados abaixo serao aplicados aos artigos selecionados.
        </Alert>

        {error && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <Grid container spacing={2}>
          <Grid item xs={12}>
            {renderTextField('title', 'T\u00edtulo')}
          </Grid>

          <Grid item xs={12}>
            {renderTextField('content', 'Conte\u00fado', {
              multiline: true,
              minRows: 5,
              placeholder: 'Digite o conte\u00fado que ser\u00e1 aplicado aos artigos selecionados'
            })}
          </Grid>

          <Grid item xs={12}>
            {renderTextField('description', 'Resumo', {
              multiline: true,
              minRows: 3,
              placeholder: 'Digite o resumo que ser\u00e1 aplicado aos artigos selecionados'
            })}
          </Grid>

          <Grid item xs={12} sm={6}>
            {renderTextField('siteName', 'Fonte/Site')}
          </Grid>

          <Grid item xs={12} sm={6}>
            {renderTextField('author', 'Autor')}
          </Grid>

          <Grid item xs={12} sm={6}>
            <Box>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={enabledFields.date}
                    onChange={() => handleToggleField('date')}
                    disabled={loading}
                  />
                }
                label="Data de publicação"
              />
              <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={ptBR}>
                <DatePicker
                  value={toLocalDate(values.date)}
                  onChange={(newValue) => {
                    setValues(prev => ({ ...prev, date: toDateValue(newValue) }));
                    setError('');
                  }}
                  disabled={!enabledFields.date || loading}
                  views={['year', 'month', 'day']}
                  openTo="day"
                  yearsPerRow={4}
                  monthsPerRow={3}
                  slots={{ calendarHeader: DatePickerCalendarHeader }}
                  format="dd/MM/yyyy"
                  slotProps={{
                    textField: {
                      fullWidth: true,
                      size: 'small'
                    }
                  }}
                />
              </LocalizationProvider>
            </Box>
          </Grid>

          <Grid item xs={12} sm={6}>
            {renderTextField('section', 'Secao/Editoria')}
          </Grid>

          <Grid item xs={12}>
            {renderTextField('image', 'URL da imagem', {
              placeholder: 'https://exemplo.com/imagem.jpg'
            })}
          </Grid>

          <Grid item xs={12}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={enabledFields.platform}
                  onChange={() => handleToggleField('platform')}
                  disabled={loading}
                />
              }
              label="Plataforma e formato"
            />
            <Grid container spacing={1.5}>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth size="small" disabled={!enabledFields.platform || loading}>
                  <InputLabel id="bulk-media-type-label">Plataforma</InputLabel>
                  <Select
                    labelId="bulk-media-type-label"
                    value={values.mediaType}
                    onChange={handleValueChange('mediaType')}
                    label="Plataforma"
                  >
                    {MEDIA_TYPE_OPTIONS.map(option => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth size="small" disabled={!enabledFields.platform || loading}>
                  <InputLabel id="bulk-content-type-label">Formato</InputLabel>
                  <Select
                    labelId="bulk-content-type-label"
                    value={values.contentType}
                    onChange={handleValueChange('contentType')}
                    label="Formato"
                  >
                    {contentTypeOptions.map(option => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            </Grid>
          </Grid>

          <Grid item xs={12}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={enabledFields.exposure}
                  onChange={() => handleToggleField('exposure')}
                  disabled={loading}
                />
              }
              label="Exposicao da marca"
            />
            <FormControl fullWidth size="small" disabled={!enabledFields.exposure || loading}>
              <InputLabel id="bulk-exposure-label">Exposicao</InputLabel>
              <Select
                labelId="bulk-exposure-label"
                value={values.exposure}
                onChange={handleValueChange('exposure')}
                label="Exposicao"
              >
                {EXPOSURE_OPTIONS.map(option => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
        </Grid>

        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 2 }}>
          {'O campo de URL ficou fora para evitar sobrescrever o endere\u00e7o \u00fanico de cada artigo.'}
        </Typography>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2.5, gap: 1 }}>
        <Button onClick={onClose} disabled={loading}>
          Cancelar
        </Button>
        <Button
          variant="contained"
          startIcon={<SaveIcon />}
          onClick={handleApply}
          disabled={loading || articleIds.length === 0}
        >
          {loading ? 'Aplicando...' : 'Aplicar em lote'}
        </Button>
      </DialogActions>
    </PlatformDialog>
  );
};

BulkEditArticlesDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  articleIds: PropTypes.arrayOf(PropTypes.string),
  loading: PropTypes.bool,
  onClose: PropTypes.func.isRequired,
  onApply: PropTypes.func.isRequired
};

export default BulkEditArticlesDialog;
